import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  Badge,
  InputGroup,
  Alert,
  Card,
} from "react-bootstrap";
import {
  IconPlus,
  IconMinus,
  IconPackage,
  IconCheck,
  IconX,
  IconShoppingCart,
  IconSearch,
  IconSelectAll,
  IconDeselect,
  IconBuildingWarehouse,
} from "@tabler/icons-react";
import { postRequest, getRequest } from "services/api";
import { useCategory } from "../context/CategoryContext";
import Swal from "sweetalert2";

const StockUpdateModalV2 = ({
  show,
  onHide,
  selectedItem,
  onSuccess,
  onError,
  warehouses = [],
}) => {
  const { categories } = useCategory();
  const [bulkUpdates, setBulkUpdates] = useState([]);
  const [bulkReason, setBulkReason] = useState("");
  const [bulkType, setBulkType] = useState("in");
  const [bulkSearchTerm, setBulkSearchTerm] = useState("");
  const [bulkCategoryFilter, setBulkCategoryFilter] = useState("");
  const [bulkStockFilter, setBulkStockFilter] = useState("all");
  const [allInventoryData, setAllInventoryData] = useState([]);
  const [allCategoriesData, setAllCategoriesData] = useState([]);
  const [bulkCurrentPage, setBulkCurrentPage] = useState(1);
  const [bulkPageSize, setBulkPageSize] = useState(20);
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch all inventory data for bulk updates
  const fetchAllInventory = useCallback(async () => {
    try {
      const response = await getRequest(
        "/inventory?limit=1000&include=category"
      );
      if (response.success) {
        const transformedData = response.data.map((item) => ({
          ...item,
          currentStock: item.current_stock_total || 0,
          warehouseStocks: item.warehouse_stocks || [],
        }));
        setAllInventoryData(transformedData);

        const initialBulkUpdates = transformedData.map((item) => ({
          id: item.id,
          name: item.name,
          category_id: item.category_id,
          unit: item.unit,
          currentStock: item.currentStock,
          quantity: 0,
          warehouse_id: selectedWarehouse || warehouses[0]?.id || "",
        }));
        setBulkUpdates(initialBulkUpdates);
      }
    } catch (error) {
      console.error("Failed to fetch inventory:", error);
    }
  }, [selectedWarehouse, warehouses]);

  // Fetch all categories for bulk updates
  const fetchAllCategories = useCallback(async () => {
    try {
      const response = await getRequest("/inventory-category?limit=1000");
      if (response.success) {
        setAllCategoriesData(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  }, []);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      const defaultWarehouse = warehouses[0]?.id || "";
      setSelectedWarehouse(defaultWarehouse);
      setBulkReason("");
      setBulkType("in");
      setBulkSearchTerm("");
      setBulkCategoryFilter("");
      setBulkStockFilter("all");
      setBulkCurrentPage(1);

      // Fetch data after setting warehouse
      fetchAllInventory();
      fetchAllCategories();
    }
  }, [show, warehouses, fetchAllInventory, fetchAllCategories]);

  // Update bulk updates when warehouse changes
  useEffect(() => {
    if (selectedWarehouse && bulkUpdates.length > 0) {
      setBulkUpdates((prev) =>
        prev.map((item) => ({
          ...item,
          warehouse_id: selectedWarehouse,
        }))
      );
    }
  }, [selectedWarehouse]);

  // Filter bulk items
  const filteredBulkItems = useMemo(() => {
    if (!bulkUpdates.length) return [];

    let filtered = bulkUpdates;

    // Search filter
    if (bulkSearchTerm.trim()) {
      const searchLower = bulkSearchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(searchLower)
      );
    }

    // Category filter
    if (bulkCategoryFilter) {
      filtered = filtered.filter(
        (item) => item.category_id === bulkCategoryFilter
      );
    }

    // Stock status filter
    if (bulkStockFilter !== "all") {
      filtered = filtered.filter((item) => {
        const originalItem = allInventoryData.find((i) => i.id === item.id);
        const selectedWarehouseStock = originalItem?.warehouseStocks?.find(
          (ws) => ws.warehouse_id === selectedWarehouse
        );
        const stock = selectedWarehouseStock?.stock || 0;
        const minStock = selectedWarehouseStock?.minimum_stock || 0;

        if (bulkStockFilter === "low") {
          return stock > 0 && stock <= minStock;
        } else if (bulkStockFilter === "empty") {
          return stock === 0;
        }
        return true;
      });
    }

    return filtered;
  }, [
    bulkUpdates,
    bulkSearchTerm,
    bulkCategoryFilter,
    bulkStockFilter,
    allInventoryData,
    selectedWarehouse,
  ]);

  // Paginate filtered items
  const paginatedBulkItems = useMemo(() => {
    const startIndex = (bulkCurrentPage - 1) * bulkPageSize;
    const endIndex = startIndex + bulkPageSize;
    return filteredBulkItems.slice(startIndex, endIndex);
  }, [filteredBulkItems, bulkCurrentPage, bulkPageSize]);

  const totalBulkPages = Math.ceil(filteredBulkItems.length / bulkPageSize);

  const handleBulkSelectAll = (checked) => {
    setBulkUpdates((prev) =>
      prev.map((item) => ({
        ...item,
        quantity: checked ? 1 : 0,
      }))
    );
  };

  const getCategoryName = (categoryId) => {
    const categoryData =
      allCategoriesData.length > 0 ? allCategoriesData : (Array.isArray(categories) ? categories : []);
    if (!categoryData.length) return "Uncategorized";
    const category = categoryData.find((cat) => cat.id === categoryId);
    return category ? category.name : "Uncategorized";
  };

  const getCurrentWarehouseStock = (itemId) => {
    const item = allInventoryData.find((i) => i.id === itemId);
    const warehouseStock = item?.warehouseStocks?.find(
      (ws) => ws.warehouse_id === selectedWarehouse
    );
    return warehouseStock?.stock || 0;
  };

  const handleBulkUpdate = async () => {
    if (!bulkReason.trim()) {
      Swal.fire(
        "Error!",
        "Please provide a reason for bulk stock update",
        "error"
      );
      return;
    }

    if (!selectedWarehouse) {
      Swal.fire("Error!", "Please select a warehouse", "error");
      return;
    }

    const selectedItems = bulkUpdates.filter((item) => item.quantity > 0);

    if (selectedItems.length === 0) {
      Swal.fire("Error!", "Please select at least one item to update", "error");
      return;
    }

    try {
      setLoading(true);

      const updates = selectedItems.map((item) => ({
        inventory_id: item.id,
        warehouse_id: selectedWarehouse,
        quantity: item.quantity,
        type: bulkType,
        reason: bulkReason.trim(),
      }));

      const payload = {
        updates,
        atomic: true,
      };

      console.log("Bulk update payload:", payload);

      const response = await postRequest(
        "/inventory/bulk-update-warehouse-stock",
        payload
      );

      if (response.success) {
        Swal.fire(
          "Success!",
          `Updated stock for ${selectedItems.length} items`,
          "success"
        );
        onSuccess && onSuccess();
      } else {
        throw new Error(response.message || "Failed to update stock");
      }
    } catch (error) {
      console.error("Bulk update error:", error);
      Swal.fire("Error!", error.message || "Failed to update stock", "error");
      onError && onError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkItemChange = (itemId, field, value) => {
    setBulkUpdates((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    );
  };

  const getStockStatus = (current, minimum) => {
    if (current === 0) {
      return {
        status: "empty",
        color: "#dc2626",
        bgColor: "#fef2f2",
        label: "Habis",
      };
    } else if (current <= minimum) {
      return {
        status: "low",
        color: "#d97706",
        bgColor: "#fffbeb",
        label: "Stok Rendah",
      };
    } else {
      return {
        status: "good",
        color: "#059669",
        bgColor: "#ecfdf5",
        label: "Stok Baik",
      };
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="workshop-modal-scope">
      <Modal.Header closeButton>
        <Modal.Title>
          <IconShoppingCart size={20} style={{ marginRight: "8px" }} />
          Bulk Update Stock V2 (Warehouse Specific)
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          <IconBuildingWarehouse size={16} style={{ marginRight: "8px" }} />
          Update stock for multiple items in a specific warehouse. Each update
          is warehouse-specific.
        </Alert>

        {/* Warehouse Selection */}
        <Form.Group className="mb-3">
          <Form.Label>Select Warehouse *</Form.Label>
          <Form.Select
            value={selectedWarehouse}
            onChange={(e) => setSelectedWarehouse(e.target.value)}
          >
            <option value="">Choose warehouse...</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name} ({warehouse.code})
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Reason for Bulk Update *</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={bulkReason}
            onChange={(e) => setBulkReason(e.target.value)}
            placeholder="Enter reason for bulk stock update (e.g., Monthly restock, Usage adjustment, Inventory correction)"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Update Type</Form.Label>
          <div className="d-flex gap-2 flex-column flex-sm-row">
            <Button
              variant={bulkType === "in" ? "success" : "outline-success"}
              onClick={() => setBulkType("in")}
              className="flex-fill"
              style={{
                padding: "12px 16px",
                fontSize: "16px",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                minHeight: "48px",
              }}
            >
              <IconPlus size={20} />
              <span>Stock In (+)</span>
            </Button>
            <Button
              variant={bulkType === "out" ? "danger" : "outline-danger"}
              onClick={() => setBulkType("out")}
              className="flex-fill"
              style={{
                padding: "12px 16px",
                fontSize: "16px",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                minHeight: "48px",
              }}
            >
              <IconMinus size={20} />
              <span>Stock Out (-)</span>
            </Button>
          </div>
        </Form.Group>

        {/* Search and Filters */}
        <Row className="mb-3 g-2">
          <Col xs={12} sm={6} md={4}>
            <InputGroup>
              <InputGroup.Text>
                <IconSearch size={16} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search items..."
                value={bulkSearchTerm}
                onChange={(e) => {
                  setBulkSearchTerm(e.target.value);
                  setBulkCurrentPage(1);
                }}
              />
            </InputGroup>
          </Col>
          <Col xs={6} sm={3} md={3}>
            <Form.Select
              value={bulkCategoryFilter}
              onChange={(e) => {
                setBulkCategoryFilter(e.target.value);
                setBulkCurrentPage(1);
              }}
            >
              <option value="">All Categories</option>
              {(allCategoriesData.length > 0
                ? allCategoriesData
                : (Array.isArray(categories) ? categories : [])
              ).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={6} sm={3} md={3}>
            <Form.Select
              value={bulkStockFilter}
              onChange={(e) => {
                setBulkStockFilter(e.target.value);
                setBulkCurrentPage(1);
              }}
            >
              <option value="all">All Stock Levels</option>
              <option value="low">Low Stock Items</option>
              <option value="empty">Out of Stock</option>
            </Form.Select>
          </Col>
          <Col xs={12} sm={12} md={2}>
            <div className="d-flex gap-1 flex-wrap">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleBulkSelectAll(true)}
                title={`${
                  bulkType === "in" ? "Add" : "Subtract"
                } quantity 1 to all filtered items`}
                className="flex-fill"
              >
                <IconSelectAll size={14} />
                <span className="d-none d-sm-inline">
                  {bulkType === "in" ? "Add" : "Subtract"} All
                </span>
                <span className="d-sm-none">All</span>
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => handleBulkSelectAll(false)}
                title="Clear all quantities"
                className="flex-fill"
              >
                <IconDeselect size={14} />
                <span className="d-none d-sm-inline">Clear All</span>
                <span className="d-sm-none">Clear</span>
              </Button>
            </div>
          </Col>
        </Row>

        {/* Results Summary */}
        <div className="mb-3 text-muted small">
          Showing {paginatedBulkItems.length} of {filteredBulkItems.length}{" "}
          items
          {bulkUpdates.filter((item) => item.quantity > 0).length > 0 && (
            <span className="ms-2">
              • {bulkUpdates.filter((item) => item.quantity > 0).length} items
              with quantity {bulkType === "in" ? "added" : "subtracted"}
            </span>
          )}
        </div>

        <Card>
          <Card.Body style={{ padding: 0 }}>
            <div className="table-responsive">
              <Table
                striped
                hover
                style={{ marginBottom: 0, minWidth: "600px" }}
              >
                <thead>
                  <tr>
                    <th style={{ minWidth: "200px" }}>Item</th>
                    <th style={{ minWidth: "120px" }}>Category</th>
                    <th style={{ textAlign: "center", minWidth: "100px" }}>
                      Current Stock
                    </th>
                    <th style={{ textAlign: "center", minWidth: "120px" }}>
                      {bulkType === "in" ? "Add" : "Subtract"} Quantity
                    </th>
                    <th style={{ textAlign: "center", minWidth: "100px" }}>
                      New Total
                    </th>
                    <th style={{ textAlign: "center", minWidth: "100px" }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedBulkItems.map((item) => {
                    const originalItem = allInventoryData.find(
                      (i) => i.id === item.id
                    );
                    const currentWarehouseStock = getCurrentWarehouseStock(
                      item.id
                    );
                    const stockStatus = getStockStatus(
                      currentWarehouseStock,
                      originalItem?.minimum_stock || 0
                    );
                    const newStock =
                      bulkType === "in"
                        ? currentWarehouseStock + item.quantity
                        : Math.max(0, currentWarehouseStock - item.quantity);
                    const hasQuantity = item.quantity > 0;

                    return (
                      <tr key={item.id}>
                        <td>
                          <div>
                            <div
                              style={{ fontWeight: "500", color: "#1f2937" }}
                            >
                              {item.name}
                            </div>
                            <small className="text-muted">({item.unit})</small>
                          </div>
                        </td>
                        <td>
                          <Badge
                            style={{
                              backgroundColor:
                                originalItem?.category?.color || "#6b7280",
                              color: "white",
                            }}
                          >
                            {getCategoryName(item.category_id)}
                          </Badge>
                        </td>
                        <td style={{ textAlign: "center", fontWeight: "500" }}>
                          {currentWarehouseStock}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <Form.Control
                            type="number"
                            min="0"
                            value={item.quantity}
                            onChange={(e) =>
                              handleBulkItemChange(
                                item.id,
                                "quantity",
                                parseInt(e.target.value) || 0
                              )
                            }
                            style={{
                              width: "80px",
                              textAlign: "center",
                              borderColor: hasQuantity ? "#0d6efd" : undefined,
                            }}
                            size="sm"
                          />
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            fontWeight: "500",
                            color: hasQuantity ? "#0d6efd" : "#1f2937",
                          }}
                        >
                          {newStock}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <Badge
                            style={{
                              backgroundColor: stockStatus.color,
                              color: "white",
                            }}
                          >
                            {stockStatus.label}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                  {paginatedBulkItems.length === 0 && (
                    <tr>
                      <td
                        colSpan="6"
                        style={{ textAlign: "center", padding: "40px" }}
                      >
                        <IconPackage size={48} color="#6b7280" />
                        <div style={{ marginTop: "16px", color: "#6b7280" }}>
                          No items match your filters
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>

          {/* Pagination */}
          {totalBulkPages > 1 && (
            <Card.Footer>
              <Row className="align-items-center g-2">
                <Col xs={12} sm={6} md={6}>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="text-muted small">Show:</span>
                    <Form.Select
                      size="sm"
                      style={{ width: "70px" }}
                      value={bulkPageSize}
                      onChange={(e) => {
                        setBulkPageSize(parseInt(e.target.value));
                        setBulkCurrentPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Form.Select>
                    <span className="text-muted small">per page</span>
                  </div>
                </Col>
                <Col xs={12} sm={6} md={6}>
                  <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
                    <span className="text-muted small d-none d-sm-inline">
                      Page {bulkCurrentPage} of {totalBulkPages}
                    </span>
                    <span className="text-muted small d-sm-none">
                      {bulkCurrentPage}/{totalBulkPages}
                    </span>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={bulkCurrentPage === 1}
                        onClick={() => setBulkCurrentPage(bulkCurrentPage - 1)}
                      >
                        ‹ Prev
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={bulkCurrentPage === totalBulkPages}
                        onClick={() => setBulkCurrentPage(bulkCurrentPage + 1)}
                      >
                        Next ›
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Footer>
          )}
        </Card>
      </Modal.Body>
      <Modal.Footer className="flex-column flex-sm-row gap-2">
        <Button
          variant="secondary"
          onClick={onHide}
          className="flex-fill flex-sm-auto"
        >
          <IconX size={16} style={{ marginRight: "8px" }} />
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleBulkUpdate}
          disabled={loading || !selectedWarehouse}
          className="flex-fill flex-sm-auto"
        >
          {loading ? (
            "Updating..."
          ) : (
            <>
              <IconCheck size={16} style={{ marginRight: "8px" }} />
              <span className="d-none d-sm-inline">Update Stock (</span>
              {bulkUpdates.filter((item) => item.quantity > 0).length} items
              <span className="d-none d-sm-inline">)</span>
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StockUpdateModalV2;
